// ============================================================================
// Bench tab — PURE SURFACE (shell owns the client and the state, same shape as
// the Insurance and Tiers tabs).
//
// The question this screen answers is "who can take a building", and the answer
// is only useful BY MARKET — a ready sub in St. Louis does not cover a building
// in Houston. So the market table comes first and the people come second.
//
// ⛔ Bench depth by market is the leading indicator. A market with no ready sub
// is a market where losing a sub means scrambling, and that has to be visible
// as a stated fact, not as the absence of a row.
// ============================================================================
import type { ReactNode } from 'react';
import { CircleCheck, CircleDashed } from 'lucide-react';
import type { BenchRow } from './hooks/use-sub-bench';

export interface MarketCover {
  market: string;
  /** Ready subs whose own market is this one, or who list it as coverage. */
  ready: number;
  /** Active subcontractors working here today. */
  working: number;
}

/** A ready sub covers their own market plus any they declared coverage for. */
export function coversMarket(r: BenchRow, market: string): boolean {
  if (r.market === market) return true;
  return r.coverage_markets.includes(market);
}

export function benchByMarket(
  bench: BenchRow[],
  markets: string[],
  workingByMarket: Record<string, number>,
): MarketCover[] {
  return markets
    .map((m) => ({
      market: m,
      ready: bench.filter((r) => coversMarket(r, m)).length,
      working: workingByMarket[m] ?? 0,
    }))
    .sort((a, b) => a.ready - b.ready || a.market.localeCompare(b.market));
}

/** Ready, but not actually reachable: the record is missing what it takes to
 *  call them. ⛔ A bench entry nobody can phone is not cover. */
export function notReachable(r: BenchRow): boolean {
  return !r.phone && !r.email;
}

export function BenchView({
  rows,
  cover,
  market,
  onMarket,
  markets,
  renderName,
}: {
  rows: BenchRow[];
  cover: MarketCover[];
  market: string;
  onMarket: (v: string) => void;
  markets: string[];
  renderName: (row: BenchRow) => ReactNode;
}) {
  const shown = market ? rows.filter((r) => coversMarket(r, market)) : rows;
  const uncovered = cover.filter((c) => c.ready === 0 && c.working > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="On the bench" value={rows.length} />
        <Tile
          label="Markets with no bench"
          value={uncovered.length}
          tone={uncovered.length ? 'risk' : undefined}
          foot={uncovered.length ? uncovered.map((c) => c.market).join(', ') : 'every market covered'}
        />
        <Tile label="Vetting returned" value={rows.filter((r) => r.intake_completed).length} />
        <Tile
          label="No way to contact them"
          value={rows.filter(notReachable).length}
          tone={rows.some(notReachable) ? 'risk' : undefined}
        />
      </div>

      {/* ---- Bench by market ------------------------------------------- */}
      <section>
        <h3 className="border-b border-tcs-border pb-1 text-sm font-semibold text-tcs-text">
          Bench by market
        </h3>
        <p className="mt-1 text-xs text-tcs-text-muted">
          A ready sub in one market does not cover a building in another. A market with nobody ready
          is a market where losing a sub means scrambling.
        </p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[28rem] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-tcs-text-muted">
              <tr>
                <th className="py-1 pr-3 font-medium">Market</th>
                <th className="py-1 pr-3 font-medium">Subs working</th>
                <th className="py-1 pr-3 font-medium">Ready on the bench</th>
                <th className="py-1 font-medium">Cover</th>
              </tr>
            </thead>
            <tbody>
              {cover.map((c) => (
                <tr key={c.market} className="border-t border-tcs-border">
                  <td className="py-1.5 pr-3 font-medium text-tcs-text">{c.market}</td>
                  <td className="py-1.5 pr-3 tabular-nums text-tcs-text-secondary">{c.working}</td>
                  <td className="py-1.5 pr-3 tabular-nums">{c.ready}</td>
                  <td className="py-1.5">
                    {c.ready === 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-st-danger-fg">
                        <CircleDashed className="h-3.5 w-3.5" /> Nobody ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-tcs-text-secondary">
                        <CircleCheck className="h-3.5 w-3.5" /> Covered
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- Who is ready ---------------------------------------------- */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border pb-1">
          <h3 className="text-sm font-semibold text-tcs-text">Who is ready</h3>
          <select
            value={market}
            onChange={(e) => onMarket(e.target.value)}
            className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1 text-sm"
            aria-label="Filter the bench by market"
          >
            <option value="">Every market</option>
            {markets.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        {shown.length === 0 ? (
          <p className="py-3 text-sm text-tcs-text-secondary">
            {market
              ? `Nobody on the bench covers ${market}. That is the finding — if the next assignment here ends, no replacement is waiting.`
              : 'Nobody is on the bench yet. A prospect reaches it once they have a name, a phone, an email and at least one capability marked.'}
          </p>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[42rem] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-tcs-text-muted">
                <tr>
                  <th className="py-1 pr-3 font-medium">Who</th>
                  <th className="py-1 pr-3 font-medium">Market</th>
                  <th className="py-1 pr-3 font-medium">Contact</th>
                  <th className="py-1 pr-3 font-medium">Vetting</th>
                  <th className="py-1 pr-3 font-medium">Crew</th>
                  <th className="py-1 pr-3 font-medium">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.provider_id} className="border-t border-tcs-border align-top">
                    <td className="py-1.5 pr-3 font-medium text-tcs-text">
                      {renderName(r)}
                      {r.owner_name && r.owner_name !== r.display_name && (
                        <div className="text-xs font-normal text-tcs-text-muted">{r.owner_name}</div>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-tcs-text-secondary">{r.market ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-xs text-tcs-text-secondary">
                      {notReachable(r) ? (
                        <span className="font-medium text-st-danger-fg">No phone or email</span>
                      ) : (
                        <>
                          {r.phone && <div>{r.phone}</div>}
                          {r.email && <div className="truncate">{r.email}</div>}
                        </>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-tcs-text-secondary">
                      {vettingLabel(r)}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-tcs-text-secondary">
                      {r.bench_depth === null ? '—' : r.bench_depth}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-tcs-text-secondary">
                      {r.days_on_bench} d
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
  foot,
}: {
  label: string;
  value: number | string;
  tone?: 'risk';
  foot?: string;
}) {
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2">
      <div
        className={`text-2xl font-bold tabular-nums ${tone === 'risk' ? 'text-st-danger-fg' : 'text-tcs-text'}`}
      >
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">{label}</div>
      {foot && <div className="mt-0.5 text-[11px] text-tcs-text-muted">{foot}</div>}
    </div>
  );
}

/**
 * How a bench sub was vetted, read from what is already recorded (TCSD-0402):
 * their application is back · they are on the work records · who put them on
 * the bench and when · otherwise not vetted yet. No approval field.
 */
export function vettingLabel(r: Pick<BenchRow, 'intake_completed' | 'worked_before' | 'benched_by' | 'benched_at'>): string {
  if (r.intake_completed) return 'Form returned';
  if (r.worked_before) return 'Worked for TCS before';
  if (r.benched_by) {
    const day = r.benched_at
      ? new Date(r.benched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' })
      : null;
    return `Put on bench by ${r.benched_by}${day ? ` · ${day}` : ''}`;
  }
  return 'Not vetted yet';
}
